import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import {
  clearSkippedSerialNumbers,
  isGiftCard,
} from '../../../../../functions/lineItem'
import {
  isMarkedAsUnfulfilled,
  isNonFulfillmentOnAccountSale,
} from '../../../../../functions/sale'
import {
  FulfilmentDetail,
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../../../models/fulfilment'
import { LineItem } from '../../../../../models/line-item'
import { useSaleManager } from '../../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks'
import { selectSaleReturnFor } from '../../../../../state/selectors'
import {
  removeFulfilmentState,
  selectCurrentSale,
  selectFulfilmentDetails,
  selectLineItems,
  selectSaleAttributesHasFulfilment,
  selectSaleCustomer,
  selectSaleId,
  setFulfilmentDetails,
  updateLineItem,
  updateLineItems,
  updateSaleStatus,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import {
  openFulfilmentLineItemPopover,
  selectIsSaleCustomerContactInfoReviewed,
} from '../../../../../state/slices/uiStateSlice'
import { useCrossLocationFulfilmentGate } from '../OutletSelect'
import { useFulfillmentHelper } from '../popover/hooks/useFulfillmentHelper'
import { fillEmptyAddressWithCustomerAddress } from '../popover/utils/addressHelper'
import { isMissingContactInfo } from './../popover/LineItemFulfillmentUtils'

type useSaleFulfilmentProps = {
  currentLineItem: LineItem
  saleId: string
  hasFulfilment: boolean
  setFulfilmentNote: (note: string) => void
  setFulfilmentType: (
    fulfilmentType: FulfilmentType,
    note?: string,
    fulfilmentOutletId?: string
  ) => void
  setFulfilmentOutletId: (fulfilmentOutletId: string) => void
  inventoryOnHandQuantity?: number
  fulfilmentDetail?: FulfilmentDetail
  isDisabled: boolean
  isFulfilmentOutletDisabled: boolean
}

export const useSaleFulfilment = (
  lineItemLocalId: string
): useSaleFulfilmentProps => {
  const { shouldFixFulfillmentAddresses } = useFulfillmentHelper()
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const isContactInfoReviewed = useAppSelector(
    selectIsSaleCustomerContactInfoReviewed
  )
  const fulfilmentDetails = useAppSelector(selectFulfilmentDetails)
  const lineItems = useAppSelector(selectLineItems)
  const saleId = useAppSelector(selectSaleId) || ''
  const { canUseCrossLocationFulfilment } = useCrossLocationFulfilmentGate()
  const t = useT()
  const currentLineItem = lineItems.find(
    item => item.localId === lineItemLocalId
  )!
  const currentSale = useAppSelector(selectCurrentSale)
  const hasFulfilment = useAppSelector(selectSaleAttributesHasFulfilment)
  const isReferencedReturn = !!useAppSelector(selectSaleReturnFor)
  const hasId = !!saleId

  const isLaybySale = !!currentSale.attributes?.includes('layby')

  const isContinuedFulfilmentSale = hasId && hasFulfilment
  const isLineItemDisabled = lineItemLocalId
    ? currentLineItem?.confirmed ||
      currentLineItem?.isReturn ||
      Number(currentLineItem?.quantity) < 0 ||
      isReferencedReturn ||
      isNonFulfillmentOnAccountSale(currentSale) ||
      isGiftCard(currentLineItem?.product) ||
      isLaybySale
    : false
  const isDisabled = lineItemLocalId
    ? isLineItemDisabled || isContinuedFulfilmentSale
    : false
  const isFulfilmentOutletDisabled = canUseCrossLocationFulfilment
    ? isLineItemDisabled ||
      (isContinuedFulfilmentSale && Boolean(currentLineItem?.id))
    : isDisabled

  const setFulfilmentType = (
    fulfilmentType: FulfilmentType,
    note?: string,
    fulfilmentOutletId?: string
  ) => {
    const otherLineItems = lineItems.filter(
      item => item.localId !== currentLineItem.localId
    )

    const shouldClearSkippedSerialNumber =
      fulfilmentType === FulfilmentType.UNSPECIFIED &&
      isDefinedFulfilmentType(currentLineItem.fulfilmentType)

    const customFields = shouldClearSkippedSerialNumber
      ? clearSkippedSerialNumbers(currentLineItem.customFields)
      : currentLineItem.customFields

    const fulfilmentOutletUpdate = canUseCrossLocationFulfilment
      ? fulfilmentType === FulfilmentType.UNSPECIFIED
        ? { fulfilmentOutletId: undefined }
        : fulfilmentOutletId !== undefined
        ? { fulfilmentOutletId }
        : {}
      : {}

    dispatch(
      updateLineItem({
        ...currentLineItem,
        fulfilmentType,
        customFields,
        ...fulfilmentOutletUpdate,
      })
    )

    // if we are moving from 'DELIVERY' to 'PICKUP' or viceversa, we need to also move the other line items
    // to keep the fulfilment type synchronized, as we only allow one fulfilment type per sale
    if (
      !!fulfilmentType &&
      fulfilmentType !== FulfilmentType.UNSPECIFIED &&
      currentLineItem.fulfilmentType !== fulfilmentType
    ) {
      const otherLineItemsWithDifferentFulfilmentType = otherLineItems.filter(
        item =>
          !!item.fulfilmentType &&
          item.fulfilmentType !== FulfilmentType.UNSPECIFIED
      )

      if (otherLineItemsWithDifferentFulfilmentType.length > 0) {
        dispatch(
          updateLineItems(
            otherLineItemsWithDifferentFulfilmentType.map(item => ({
              ...item,
              fulfilmentType,
            }))
          )
        )

        if (fulfilmentType !== fulfilmentDetails[0]?.type) {
          ToastNotifications.success(
            t(
              'Fulfillment method updated for all items that require fulfillment'
            )
          )
        }
      }
    }

    // if all line items are deselected, we remove the fulfilment details and reset the sale status
    if (
      isMarkedAsUnfulfilled(currentSale.status) &&
      fulfilmentType === FulfilmentType.UNSPECIFIED &&
      lineItems
        .filter(item => item.localId !== lineItemLocalId)
        .every(
          item =>
            !item.fulfilmentType ||
            item.fulfilmentType === FulfilmentType.UNSPECIFIED
        )
    ) {
      dispatch(removeFulfilmentState())
      dispatch(updateSaleStatus(undefined))
    }

    if (fulfilmentType !== FulfilmentType.UNSPECIFIED) {
      // Clear the address if old type was non-fulfilment so old customer address does not stay in state
      // fillEmptyAddressWithCustomerAddress below will populate the address if needed
      const previousType = fulfilmentDetails[0]?.type
      const preserveAddresses = isDefinedFulfilmentType(previousType)
      const newFulfilmentDetail: FulfilmentDetail = {
        type: fulfilmentType,
        billingAddress: preserveAddresses
          ? fulfilmentDetails[0]?.billingAddress
          : undefined,
        shippingAddress: preserveAddresses
          ? fulfilmentDetails[0]?.shippingAddress
          : undefined,
        note: note || '',
      }
      if (fulfilmentType === FulfilmentType.DELIVERY && saleCustomer) {
        const {
          billingAddress,
          shippingAddress,
        } = fillEmptyAddressWithCustomerAddress(
          saleCustomer,
          newFulfilmentDetail.billingAddress,
          newFulfilmentDetail.shippingAddress
        )
        newFulfilmentDetail.billingAddress = billingAddress
        newFulfilmentDetail.shippingAddress = shippingAddress

        if (shouldFixFulfillmentAddresses(newFulfilmentDetail)) {
          dispatch(openFulfilmentLineItemPopover(lineItemLocalId))
        }
      }
      dispatch(setFulfilmentDetails([newFulfilmentDetail]))
    }

    switch (fulfilmentType) {
      case FulfilmentType.PICKUP:
        saleManager.unfulfilledPickUp()
        break
      case FulfilmentType.DELIVERY:
        saleManager.unfulfilledDelivery()
        break
      default:
        break
    }

    if (
      !saleCustomer ||
      isMissingContactInfo(saleCustomer) ||
      !isContactInfoReviewed
    ) {
      if (fulfilmentType !== FulfilmentType.UNSPECIFIED) {
        dispatch(openFulfilmentLineItemPopover(lineItemLocalId))
      } else {
        dispatch(openFulfilmentLineItemPopover(''))
      }
    }

    saleManager.saveSale()
  }

  const setFulfilmentOutletId = (fulfilmentOutletId: string) => {
    if (
      isFulfilmentOutletDisabled ||
      !canUseCrossLocationFulfilment ||
      !isDefinedFulfilmentType(currentLineItem.fulfilmentType)
    ) {
      return
    }

    dispatch(
      updateLineItem({
        ...currentLineItem,
        fulfilmentOutletId,
      })
    )
    saleManager.saveSale()
  }

  const setFulfilmentNote = (note: string) => {
    dispatch(setFulfilmentDetails([{ ...fulfilmentDetails[0], note }]))
  }

  return {
    currentLineItem,
    saleId,
    hasFulfilment,
    setFulfilmentType,
    setFulfilmentOutletId,
    setFulfilmentNote,
    fulfilmentDetail: fulfilmentDetails[0],
    isDisabled,
    isFulfilmentOutletDisabled,
  }
}
